"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.evalRule = void 0;
var Schedules_1 = require("./Schedules");
var helpers_1 = require("./helpers");
function evalRule(rule, target, rulesDataStore, isMultitimezoneEnabled) {
    // If rule is not defined, always return true.
    if (!rule) {
        return true;
    }
    if ((0, helpers_1.isRuleOperatorType)(rule)) {
        return evalOperatorRule(rule, target, rulesDataStore);
    }
    else if ((0, helpers_1.isRuleGroupType)(rule)) {
        return evalGroupRule(rule, target, rulesDataStore, isMultitimezoneEnabled);
    }
    else if ((0, helpers_1.isRuleScheduleType)(rule)) {
        return evalScheduleRule(rule, isMultitimezoneEnabled);
    }
    else {
        throw new Error("Invalid rule '".concat(JSON.stringify(rule), "'"));
    }
}
exports.evalRule = evalRule;
function evalOperatorRule(rule, target, rulesDataStore) {
    // Return true if target is a playlist but the rule references a presentation only variable (ie. applicationName).
    if (!(0, helpers_1.isPresentation)(target) && referencesPresentationOnlyVariable(rule)) {
        return true;
    }
    var scope = getRuleVariableScope(target, rulesDataStore);
    var leftValue = unwrapValue(rule.left, scope);
    var rightValue = unwrapValue(rule.right, scope);
    // This will happen when the tags value is null on either left or right side or both
    // If any tag value is null, then the rule should always evaluates to false
    if (leftValue === null || rightValue === null) {
        return false;
    }
    switch (rule.type) {
        case 'equals':
            // comparing two arrays for equal
            if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
                return (0, helpers_1.isArrayEqual)(leftValue, rightValue);
            }
            return leftValue === rightValue;
        case 'notEquals':
            if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
                return !(0, helpers_1.isArrayEqual)(leftValue, rightValue);
            }
            return leftValue !== rightValue;
        case 'contains':
            // both leftValue and rightValue are Array
            if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
                return (0, helpers_1.isIntersection)(leftValue, rightValue);
            }
            return (0, helpers_1.supportsIndexOf)(leftValue) && leftValue.indexOf(rightValue) !== -1;
        case 'notContains':
            // both leftValue and rightValue are Array
            if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
                return !(0, helpers_1.isIntersection)(leftValue, rightValue);
            }
            return (0, helpers_1.supportsIndexOf)(leftValue) && leftValue.indexOf(rightValue) === -1;
        case 'greaterThan':
            return leftValue > rightValue;
        case 'greaterThanOrEqual':
            return leftValue >= rightValue;
        case 'lessThan':
            return leftValue < rightValue;
        case 'lessThanOrEqual':
            return leftValue <= rightValue;
        default:
            return false;
    }
}
function referencesPresentationOnlyVariable(rule) {
    var presentationOnlyVariables = ['hasPlaylists', 'applicationName'];
    var referencesPresentationVariable = function (variable) {
        return (presentationOnlyVariables.indexOf(String(variable[variable.length - 1])) !== -1);
    };
    if ('var' in rule.left && referencesPresentationVariable(rule.left.var)) {
        return true;
    }
    if ('var' in rule.right && referencesPresentationVariable(rule.right.var)) {
        return true;
    }
    return false;
}
function unwrapValue(value, scope) {
    if ('var' in value) {
        return getRuleVariableValue(value.var, scope);
    }
    else if ('const' in value) {
        return value.const;
    }
}
function getRuleVariableScope(target, rulesDataStore) {
    if (rulesDataStore === void 0) { rulesDataStore = {}; }
    return {
        target: {
            applicationName: (0, helpers_1.isPresentation)(target)
                ? target.applicationName || ''
                : undefined,
            hasPlaylists: (0, helpers_1.isPresentation)(target)
                ? target.hasPlaylists || false
                : undefined,
            tags: target.tags || {}
        },
        device: rulesDataStore.device || {},
        workers: rulesDataStore.workers
    };
}
function getRuleVariableValue(path, scope) {
    // returning current time/date/day for Time & Date rules (right value)
    if (path.length && path[0] === 'time_and_date') {
        var currDate = new Date();
        switch (path[1]) {
            case 'curr_time':
                return currDate.toTimeString().slice(0, 5);
            case 'curr_date':
                return Date.parse(currDate.toLocaleDateString());
            case 'curr_day':
                // converting this to a single element array for generic rule evaluation for arrays
                return [helpers_1.weekdays[currDate.getDay()]];
        }
    }
    var value = scope;
    // Special case for 'start_date' schelding tag (left value)
    if (path.length === 3 && path[2] === 'start_date') {
        // using Date.parse to convert the date string to epoch time for later comparison
        return Date.parse(new Date(value.target.tags.start_date).toLocaleDateString());
    }
    // Special case for 'end_date' schelding tag (left value)
    if (path.length === 3 && path[2] === 'end_date') {
        return Date.parse(value.target.tags.end_date);
    }
    var i = 0;
    for (; i < path.length; i++) {
        var key = path[i];
        if (value && typeof value === 'object' && key in value) {
            value = value[key];
        }
        else {
            break;
        }
    }
    // For Multi value tags
    // Splitting the string with | and triming the trailing spaces if any
    if (typeof value === 'string' && value.includes('|')) {
        value = value.split('|').map(function (item) {
            return item.trim();
        });
        return value;
    }
    // Only return value if we've successfully traversed the entire path.
    if (i === path.length) {
        if (typeof value === 'string') {
            return value.trim();
        }
        return value;
    }
    // TODO: Throw invalid path error instead?
    return null;
}
function evalGroupRule(rule, target, rulesDataStore, isMultitimezoneEnabled) {
    for (var i = 0; i < rule.group.length; i++) {
        var innerRule = rule.group[i];
        var innerRuleResult = evalRule(innerRule, target, rulesDataStore, isMultitimezoneEnabled);
        // Return early if the inner rule result is true and we are evaluating an 'or' type.
        if (rule.type === 'or' && innerRuleResult) {
            return true;
        }
        // Return early if the inner rule result is false and we are evaluating an 'and' type.
        if (rule.type === 'and' && !innerRuleResult) {
            return false;
        }
    }
    if (rule.type === 'and') {
        // If we've looped through the set of rules and we are evaluating and 'and' type that means
        // all inner rule results were true so return true.
        return true;
    }
    else {
        // If we've looped through the set of rules and we are evaluating and 'or' type that means
        // all inner rule results were false so return false.
        return false;
    }
}
function evalScheduleRule(rule, isMultitimezoneEnabled) {
    return (0, Schedules_1.isScheduleActive)(rule, isMultitimezoneEnabled);
}
